import classNames from 'classnames';
import React, { FC, MouseEventHandler, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Close from '../../../Icons/Close.svg';
import { WithTranslations, withTranslations } from '../../../utils/withTranslations';
import { injectStoresV2 } from '../../stores/injectStoresV2';
import { getEditorLink } from '../../../utils/templateLinks';
import { useEditorSessionId } from '../../contexts/EditorSessionIdContext';
import { RoutingStore } from '../../stores/RoutingStore';
import { BILoggerStore } from '../../stores/BILoggerStore';
import { TemplatesStore } from '../../stores/TemplatesStore';
import Chevron from './chevron.svg';
import s from './ViewModal.scss';
import { ModeToggle } from './ModeToggle';
import { ViewModalStore } from './ViewModalStore';
import { InfoModal } from './InfoModal';

type Props = WithTranslations & {
  viewModalStore: ViewModalStore;
  routingStore: RoutingStore;
  biLoggerStore: BILoggerStore;
  templatesStore: TemplatesStore;
};

const ViewModalCmp: FC<Props> = ({ t, viewModalStore, routingStore, biLoggerStore, templatesStore }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [showInfo, setShowInfo] = useState(false);
  const { prevTemplate, template, index, nextTemplate, mode } = viewModalStore.modalState || {};

  const handleClose = () => {
    if (!template) {
      return;
    }
    void biLoggerStore.logCloseTemplateModal({ siteId: template.siteId });
    return viewModalStore.closeTemplate();
  };

  const handleDialogClick: MouseEventHandler<HTMLDialogElement> = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) {
      return;
    }

    if (template?.metaSiteId) {
      el.showModal();
    } else if (el.open) {
      el.close();
    }
  }, [template]);

  useLayoutEffect(() => {
    const el = dialogRef.current;
    if (!el) {
      return;
    }
    el.classList.add(s.loading);

    const iframe = el.querySelector(`.${s.current} iframe`) as HTMLIFrameElement | null;
    if (!iframe) {
      return;
    }

    const handleLoad = () => {
      el.classList.remove(s.loading);

      const win = iframe.contentWindow;
      if (!win) {
        return;
      }

      const handleScroll = () => {
        if (win.scrollY > 0) {
          el.classList.add(s.scrolled);
        } else {
          el.classList.remove(s.scrolled);
        }
      };
      win.addEventListener('scroll', handleScroll);
    };

    iframe.addEventListener('load', handleLoad);

    return () => {
      el.classList.remove(s.scrolled);
      el.classList.remove(s.loading);
      iframe.removeEventListener('scroll', handleLoad);
    };
  }, [template, mode]);

  const { editorSessionId, refreshEditorSessionId } = useEditorSessionId();
  const { petri_ovr, dashboardFirstMetaSiteId, siteCreationFolderId, dashboardFirst } =
    routingStore.getGlobalUrlParams();
  const editorLink = template
    ? getEditorLink({
        template,
        editorSessionId,
        petri_ovr,
        dashboardFirstMetaSiteId,
        siteCreationFolderId,
        dashboardFirst,
      })
    : undefined;

  const handleEditClick = (origin: 'modal_info' | 'modal_top') => {
    if (!template) {
      return;
    }
    const { siteId, metaSiteId, score } = template;
    void biLoggerStore.logTemplateEdit({
      siteId,
      galleryDocIndex: index,
      openingBrowserUrl: editorLink,
      metaSiteId,
      editorSessionId,
      hasVideo: !!template.videoSrc,
      origin,
      score,
      currentPageNumber: routingStore.currentPage,
      isBestMatch: templatesStore.bestMatchItemIds.includes(template.id),
    });

    setTimeout(() => refreshEditorSessionId());
  };

  const handlePrev = () => {
    if (!template || !prevTemplate) {
      return;
    }
    void biLoggerStore.logNavigate({
      fromTemplateId: template.siteId,
      toTemplateId: prevTemplate.siteId,
      fromTemplateIndex: index,
      toTemplateIndex: index - 1,
      direction: 'left',
    });
    return viewModalStore.next(-1);
  };

  const handleNext = () => {
    if (!template || !nextTemplate) {
      return;
    }
    void biLoggerStore.logNavigate({
      fromTemplateId: template.siteId,
      toTemplateId: nextTemplate.siteId,
      fromTemplateIndex: index,
      toTemplateIndex: index + 1,
      direction: 'right',
    });
    return viewModalStore.next(1);
  };

  const handleModeToggleChange = () => {
    if (!template) {
      return;
    }
    const nextMode = viewModalStore.toggleMode();
    if (nextMode === 'desktop') {
      void biLoggerStore.logViewDesktop({ siteId: template.siteId, templateIndex: index });
    }
    if (nextMode === 'mobile') {
      void biLoggerStore.logViewMobile({ siteId: template.siteId, templateIndex: index });
    }
  };

  const handleInfoClick = () => {
    void biLoggerStore.logReadMore({ siteId: template.siteId, templateIndex: index });
    return setShowInfo(true);
  };

  return (
    <dialog ref={dialogRef} className={s.dialog} onClick={handleDialogClick}>
      <button onClick={handlePrev} disabled={!viewModalStore.canNext(-1)}>
        <Chevron transform="rotate(-180)" />
      </button>
      <div className={classNames(s.content, s[mode])}>
        <header>
          <button className={s.close} onClick={handleClose}>
            <Close width={12} height={12} fill="#151414" />
          </button>
          <ModeToggle value={mode} onChange={handleModeToggleChange} />
          <div className={s.right}>
            <button className={s.infoButton} onClick={handleInfoClick}>
              {t('viewModal.moreInfo')}
            </button>
            <a className={s.editButton} href={editorLink} onClick={() => handleEditClick('modal_top')}>
              {t('viewModal.edit')}
            </a>
          </div>
          <InfoModal
            template={showInfo ? template : undefined}
            onEditorClick={() => handleEditClick('modal_info')}
            editorLink={editorLink}
            onClose={() => setShowInfo(false)}
          />
        </header>
        <main>
          {prevTemplate?.metaSiteId && (
            <iframe
              key={prevTemplate.metaSiteId + mode}
              aria-hidden
              style={{ display: 'none' }}
              src={`/website/templates/api/preview/${prevTemplate.metaSiteId}/${mode}`}
              title={prevTemplate.title}
              loading="lazy"
            />
          )}
          <div className={s.current}>
            {template?.metaSiteId && (
              <iframe
                key={template.metaSiteId + mode}
                src={`/website/templates/api/preview/${template.metaSiteId}/${mode}`}
                title={template.title}
              />
            )}
          </div>
          {nextTemplate?.metaSiteId && (
            <iframe
              key={nextTemplate.metaSiteId + mode}
              aria-hidden
              style={{ display: 'none' }}
              src={`/website/templates/api/preview/${nextTemplate.metaSiteId}/${mode}`}
              title={nextTemplate.title}
              loading="lazy"
            />
          )}
        </main>
      </div>
      <button onClick={handleNext} disabled={!viewModalStore.canNext(1)}>
        <Chevron />
      </button>
    </dialog>
  );
};

export const ViewModal = withTranslations(
  injectStoresV2('viewModalStore', 'routingStore', 'biLoggerStore', 'templatesStore')(ViewModalCmp),
);
